import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { API_URL } from '../utils/Constants/URLS'
import { X, Plus, Trash2, CheckCircle } from 'lucide-react'

const emptyWorkItem = { description: '', rate: '', unit: '', totalArea: '', totalValue: '' }

const CreateWorkOrder = ({ isOpen, onClose, selectedDoc }) => {
  const [vendor, setVendor] = useState('')
  const [quotation, setQuotation] = useState('')
  const [gsplGstin, setGsplGstin] = useState('')
  const [workItems, setWorkItems] = useState([emptyWorkItem])
  const [scopeOfWork, setScopeOfWork] = useState('')
  const [exclusions, setExclusions] = useState('')
  const [billingAddress, setBillingAddress] = useState('')
  const [siteAddress, setSiteAddress] = useState('')
  const [paymentSchedule, setPaymentSchedule] = useState('')
  const [billingSchedule, setBillingSchedule] = useState('')
  const [extraSpecifications, setExtraSpecifications] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const token = useSelector((state) => state.user?.token) || localStorage.getItem('token')

  useEffect(() => {
    if (isOpen) {
      if (selectedDoc) {
        setVendor(selectedDoc?.orderGivenTo?.vendor?._id || '')
        setQuotation(selectedDoc?._id || '')
      }
    } else {
      // Reset state when closed
      setVendor('')
      setQuotation('')
      setGsplGstin('')
      setWorkItems([emptyWorkItem])
      setScopeOfWork('')
      setExclusions('')
      setBillingAddress('')
      setSiteAddress('')
      setPaymentSchedule('')
      setBillingSchedule('')
      setExtraSpecifications('')
      setErrors({})
    }
  }, [isOpen, selectedDoc])

  if (!isOpen) return null

  const vendorName = selectedDoc?.orderGivenTo?.vendor?.vendorName || 'Unknown Vendor'

  const updateWorkItem = (idx, field, value) => {
    setWorkItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        const updatedItem = { ...item, [field]: value };
        if (field === 'rate' || field === 'totalArea') {
            const r = parseFloat(updatedItem.rate) || 0;
            const a = parseFloat(updatedItem.totalArea) || 0;
            updatedItem.totalValue = Number((r * a).toFixed(2));
        }
        return updatedItem;
      })
    )
    if (errors[`item_${field}_${idx}`]) {
      setErrors(p => ({ ...p, [`item_${field}_${idx}`]: '' }))
    }
    if ((field === 'rate' || field === 'totalArea') && errors[`item_value_${idx}`]) {
      setErrors(p => ({ ...p, [`item_value_${idx}`]: '' }))
    }
  }
  const addWorkItem = () => setWorkItems((prev) => [...prev, emptyWorkItem])
  const removeWorkItem = (idx) => setWorkItems((prev) => prev.filter((_, i) => i !== idx))

  const validate = () => {
    const e = {}
    if (!vendor) e.vendor = 'Required'
    if (!quotation) e.quotation = 'Required'
    if (!scopeOfWork.trim()) e.scopeOfWork = 'Required'
    if (!exclusions.trim()) e.exclusions = 'Required'
    if (!billingAddress.trim()) e.billingAddress = 'Required'
    if (!siteAddress.trim()) e.siteAddress = 'Required'
    if (!paymentSchedule.trim()) e.paymentSchedule = 'Required'
    if (!billingSchedule.trim()) e.billingSchedule = 'Required'
    if (!extraSpecifications.trim()) e.extraSpecifications = 'Required'

    workItems.forEach((item, idx) => {
      if (!item.description.trim()) e[`item_desc_${idx}`] = 'Required'
      if (!item.rate || Number(item.rate) <= 0) e[`item_rate_${idx}`] = 'Invalid'
      if (!item.unit.trim()) e[`item_unit_${idx}`] = 'Required'
      if (!item.totalArea || Number(item.totalArea) <= 0) e[`item_area_${idx}`] = 'Invalid'
      if (!item.totalValue || Number(item.totalValue) <= 0) e[`item_value_${idx}`] = 'Invalid'
    })
    return e
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      const firstKey = Object.keys(validationErrors)[0]
      toast.warn(`Please fix validation error: ${firstKey} - ${validationErrors[firstKey]}`)
      return
    }

    setErrors({})
    const payload = {
      vendor,
      quotation,
      gsplGstin,
      workItems: workItems.map(item => ({
        description: item.description.trim(),
        rate: Number(item.rate),
        unit: item.unit.trim(),
        totalArea: Number(item.totalArea),
        totalValue: Number(item.totalValue),
      })),
      scopeOfWork: scopeOfWork.trim(),
      exclusions: exclusions.trim(),
      billingAddress: billingAddress.trim(),
      siteAddress: siteAddress.trim(),
      paymentSchedule: paymentSchedule.trim(),
      billingSchedule: billingSchedule.trim(),
      extraSpecifications: extraSpecifications.trim()
    }

    setLoading(true)
    try {
      const res = await fetch(`${API_URL}work-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })
      const result = await res.json()
      if (result.error === false) {
        toast.success('Work order created successfully!')
        onClose()
      } else {
        toast.error(result.message || 'Failed to create work order')
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const Label = ({ children, required, error }) => (
    <label className="block text-sm font-semibold text-slate-700 mb-1 mt-3">
      {children} {required && <span className="text-rose-500">*</span>}
      {error && <span className="ml-2 text-xs font-normal text-rose-500">{error}</span>}
    </label>
  )

  const inputClass = (err, isTable = false) => `w-full ${isTable ? 'px-2' : 'px-4'} py-2 bg-slate-50 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm text-slate-800 ${err ? 'border-rose-400 focus:ring-rose-500' : 'border-slate-200'}`

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 sm:p-6 backdrop-blur-sm bg-slate-900/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white backdrop-blur">
          <div>
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-600 p-1.5 rounded-lg">
                <CheckCircle size={20} />
              </span>
              Create Work Order
            </h2>
            <p className="text-xs text-slate-500 mt-1 font-medium m-0">Issue a detailed work order for services</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors border-0 bg-transparent">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 bg-white" style={{ maxHeight: 'calc(95vh - 140px)' }}>
          <form id="wo-form" onSubmit={handleSubmit} className="space-y-8">
            
            {/* Meta Section */}
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
              <Label>Assigned Vendor</Label>
              <div className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 font-semibold shadow-sm">
                {vendorName}
              </div>

              <Label required error={errors.gsplGstin}>GSPL GSTIN</Label>
              <select
                className={inputClass(errors.gsplGstin)}
                value={gsplGstin}
                onChange={e => { setGsplGstin(e.target.value); setErrors(p => ({ ...p, gsplGstin: '' })) }}
              >
                <option value=''>-- Select GSPL GSTIN --</option>
                <option value='19AAECG1743K1ZG'>19AAECG1743K1ZG</option>
                <option value='18AAECG1743K1ZI'>18AAECG1743K1ZI</option>
              </select>
            </div>

            {/* Work Items Table */}
            <div>
              <div className="flex justify-between items-center mb-4 mt-6">
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide m-0">Work Items</h3>
                <button type="button" onClick={addWorkItem} className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg border-0 hover:bg-blue-100 flex items-center gap-1 transition-colors">
                  <Plus size={14} /> Add Item
                </button>
              </div>
              
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left m-0">
                    <thead className="bg-slate-100 text-slate-600 font-semibold text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 min-w-[200px] font-semibold text-slate-600">Description</th>
                        <th className="px-4 py-3 w-32 font-semibold text-slate-600">Rate (₹)</th>
                        <th className="px-4 py-3 w-28 font-semibold text-slate-600">Unit</th>
                        <th className="px-4 py-3 w-32 font-semibold text-slate-600">Area</th>
                        <th className="px-4 py-3 w-32 font-semibold text-slate-600">Value (₹)</th>
                        <th className="px-4 py-3 w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {workItems.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                          <td className="p-3">
                            <input className={inputClass(errors[`item_desc_${idx}`], true)} value={item.description} onChange={e => updateWorkItem(idx, 'description', e.target.value)} />
                          </td>
                          <td className="p-3">
                            <input type="number" onWheel={e => e.target.blur()} min="0" step="0.01" className={inputClass(errors[`item_rate_${idx}`], true)} value={item.rate} onChange={e => updateWorkItem(idx, 'rate', e.target.value)} />
                          </td>
                          <td className="p-3">
                            <input className={inputClass(errors[`item_unit_${idx}`], true)} value={item.unit} onChange={e => updateWorkItem(idx, 'unit', e.target.value)} />
                          </td>
                          <td className="p-3">
                            <input type="number" onWheel={e => e.target.blur()} min="0" step="0.01" className={inputClass(errors[`item_area_${idx}`], true)} value={item.totalArea} onChange={e => updateWorkItem(idx, 'totalArea', e.target.value)} />
                          </td>
                          <td className="p-3">
                            <input readOnly type="number" min="0" step="0.01" className={`${inputClass(errors[`item_value_${idx}`], true)} bg-slate-100 opacity-80 cursor-not-allowed`} value={item.totalValue} />
                          </td>
                          <td className="p-3 text-center">
                            <button type="button" onClick={() => removeWorkItem(idx)} disabled={workItems.length <= 1} className={`p-2 rounded-lg border-0 bg-transparent ${workItems.length > 1 ? 'text-rose-500 hover:bg-rose-50' : 'text-slate-300'}`}>
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Terms and Scope Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <Label required error={errors.scopeOfWork}>Scope of Work</Label>
                <textarea rows="4" className={`resize-y ${inputClass(errors.scopeOfWork)}`} value={scopeOfWork} onChange={e => { setScopeOfWork(e.target.value); setErrors(p => ({...p, scopeOfWork: ''})) }} />
              </div>
              <div>
                <Label required error={errors.exclusions}>Exclusions</Label>
                <textarea rows="4" className={`resize-y ${inputClass(errors.exclusions)}`} value={exclusions} onChange={e => { setExclusions(e.target.value); setErrors(p => ({...p, exclusions: ''})) }} />
              </div>
              
              <div>
                <Label required error={errors.billingAddress}>Billing Address</Label>
                <textarea rows="4" className={`resize-y ${inputClass(errors.billingAddress)}`} value={billingAddress} onChange={e => { setBillingAddress(e.target.value); setErrors(p => ({...p, billingAddress: ''})) }} />
              </div>
              <div>
                <Label required error={errors.siteAddress}>Site Address</Label>
                <textarea rows="4" className={`resize-y ${inputClass(errors.siteAddress)}`} value={siteAddress} onChange={e => { setSiteAddress(e.target.value); setErrors(p => ({...p, siteAddress: ''})) }} />
              </div>

              <div>
                <Label required error={errors.paymentSchedule}>Payment Schedule</Label>
                <textarea rows="4" className={`resize-y ${inputClass(errors.paymentSchedule)}`} value={paymentSchedule} onChange={e => { setPaymentSchedule(e.target.value); setErrors(p => ({...p, paymentSchedule: ''})) }} />
              </div>
              <div>
                <Label required error={errors.billingSchedule}>Billing Schedule</Label>
                <textarea rows="4" className={`resize-y ${inputClass(errors.billingSchedule)}`} value={billingSchedule} onChange={e => { setBillingSchedule(e.target.value); setErrors(p => ({...p, billingSchedule: ''})) }} />
              </div>

              <div className="md:col-span-2">
                <Label required error={errors.extraSpecifications}>Extra Specifications</Label>
                <textarea rows="4" className={`resize-y ${inputClass(errors.extraSpecifications)}`} value={extraSpecifications} onChange={e => { setExtraSpecifications(e.target.value); setErrors(p => ({...p, extraSpecifications: ''})) }} />
              </div>
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} disabled={loading} className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:text-slate-800 transition-colors">
            Cancel
          </button>
          <button type="submit" form="wo-form" disabled={loading} className="px-5 py-2.5 text-sm font-bold text-white bg-blue-600 border-0 rounded-lg hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center gap-2 shadow-sm shadow-blue-200">
            {loading ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
            ) : (
              <CheckCircle size={16} />
            )}
            {loading ? 'Creating Work Order...' : 'Create Work Order'}
          </button>
        </div>
        
      </div>
    </div>
  )
}

export default CreateWorkOrder